import React, { useEffect, useState } from "react";
import { fetchData } from "../../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  AddButton,
  CustomConfirmModal,
  PaginationDropdown,
  ThinDropdownWithSearch,
} from "../../../components/common/GeneralComponent";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Pagination,
} from "@mui/material";
import DataTable from "react-data-table-component";
import { ChevronsRightIcon, Minus, Plus, Download } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../../components/partials/Loader";
import * as XLSX from "xlsx";

import GeneralFooter from "../../common/GeneralFooter";
import PermissionButton from "../../common/PermissionButton";

const Village = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  const [villages, setVillages] = useState([]);
  const [search, setSearch] = useState("");
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedVillageForDelete, setSelectedVillageForDelete] =
    useState(null);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);

  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [ucs, setUcs] = useState([]);
  const [dehs, setDehs] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedTehsil, setSelectedTehsil] = useState(null);
  const [selectedUc, setSelectedUc] = useState(null);
  const [selectedDeh, setSelectedDeh] = useState(null);

  const [tempDistrict, setTempDistrict] = useState(selectedDistrict);
  const [tempTehsil, setTempTehsil] = useState(selectedTehsil);
  const [tempUc, setTempUc] = useState(selectedUc);
  const [tempDeh, setTempDeh] = useState(selectedDeh);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  const [loader, setLoader] = useState(true);
  const fetchVillages = async () => {
    try {
      const response = await fetchData(
        "all-villages",
        "GET",
        null,
        {},
        {
          search: search || undefined,
          page: pagination.currentPage,
          limit: pagination.pageSize,
          district_id: selectedDistrict || undefined,
          tehsil_id: selectedTehsil || undefined,
          uc_id: selectedUc || undefined,
          deh_id: selectedDeh || undefined,
        },
        navigate,
        "/villages",
        dispatch
      );
      setVillages(response?.data || []);
      setPagination(response?.pagination || {});
    } catch (error) {
      console.error("Error fetching villages:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchVillages();
  }, [
    pagination.currentPage,
    pagination.pageSize,
    search,
    selectedDistrict,
    selectedTehsil,
    selectedUc,
    selectedDeh,
  ]);
  const fetchDistricts = async () => {
    try {
      const res = await fetchData(
        "districts",
        "GET",
        null,
        {},
        {},
        navigate,
        "/districts",
        dispatch
      );
      setDistricts(res?.data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };
  const fetchTehsils = async () => {
    if (!tempDistrict) return;
    try {
      const res = await fetchData(
        "tehsils",
        "GET",
        null,
        {},
        { district_id: tempDistrict },
        navigate,
        "/tehsils",
        dispatch
      );
      setTehsils(res?.data || []);
    } catch (error) {
      console.error("Error fetching tehsils:", error);
    }
  };
  const fetchUcs = async () => {
    if (!tempTehsil) return;
    try {
      const res = await fetchData(
        "ucs",
        "GET",
        null,
        {},
        { tehsil_id: tempTehsil },
        navigate,
        "/ucs",
        dispatch
      );
      setUcs(res?.data || []);
    } catch (error) {
      console.error("Error fetching UCs:", error);
    }
  };
  const fetchDehs = async () => {
    if (!tempTehsil) return;
    try {
      const res = await fetchData(
        "dehs",
        "GET",
        null,
        {},
        { tehsil_id: tempTehsil },
        navigate,
        "/dehs",
        dispatch
      );
      setDehs(res?.data || []);
    } catch (error) {
      setDehs([]);
    }
  };

  useEffect(() => {
    fetchDistricts();
  }, []);
  useEffect(() => {
    fetchTehsils();
  }, [tempDistrict]);
  useEffect(() => {
    fetchUcs();
  }, [tempTehsil]);
  useEffect(() => {
    fetchDehs();
  }, [tempTehsil]);
  const applyFilters = () => {
    setSelectedDistrict(tempDistrict);
    setSelectedTehsil(tempTehsil);
    setSelectedUc(tempUc);
    setSelectedDeh(tempDeh);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const clearTempFilters = () => {
    setTempDistrict(null);
    setTempTehsil(null);
    setTempUc(null);
    setTempDeh(null);
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedDistrict(null);
    setSelectedTehsil(null);
    setSelectedUc(null);
    setSelectedDeh(null);
    clearTempFilters();
    setTehsils([]);
    setUcs([]);
    setDehs([]);
    setDistricts([]);
    fetchDistricts();
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };
  const handleDeleteConfirm = async () => {
    if (!selectedVillageForDelete) return;
    setIsDeleting(true);
    try {
      await fetchData(
        "villages",
        "DELETE",
        { village_id: selectedVillageForDelete.id },
        {},
        {},
        navigate,
        "/villages",
        dispatch
      );
      toast.success("Village deleted successfully.");
      setModalVisible(false);
      setSelectedVillageForDelete(null);
      await fetchVillages();
    } catch (error) {
      console.error("Error deleting Village:", error);
      toast.error(error?.message || "Failed to delete Village.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setModalVisible(false);
    setSelectedVillageForDelete(null);
  };

  const showDeleteModal = (row) => {
    setSelectedVillageForDelete({ id: row.village_id, name: row.village_name });
    setModalVisible(true);
  };

  const toggleFilters = () => setIsFiltersVisible((prev) => !prev);

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const response = await fetchData(
        "all-villages",
        "GET",
        null,
        {},
        {
          search: search || undefined,
          district_id: selectedDistrict || undefined,
          tehsil_id: selectedTehsil || undefined,
          uc_id: selectedUc || undefined,
          deh_id: selectedDeh || undefined,
          limit: 999999,
          page: 1,
        },
        navigate,
        "/villages",
        dispatch
      );
      const exportData = response?.data || [];
      if (!exportData.length) {
        toast.warning("No data to export");
        return;
      }
      const excelData = exportData.map((village, index) => ({
        "Sr.No": index + 1,
        "Village Name": village.village_name || "-",
        VID: village.vid || "-",
        Type: village.type || "-",
        "Deh Name": getNestedValue(village, [
          "Deh.deh_name",
          "deh.deh_name",
          "deh_name",
        ]),
        // "UC Name": getNestedValue(village, [
        //   "UC.uc_name",
        //   "uc.uc_name",
        //   "UC.UC_Name",
        //   "uc.UC_Name",
        //   "uc_name",
        // ]),
        "Tehsil Name": getNestedValue(village, [
          "Tehsil.tehsil_name",
          "tehsil.tehsil_name",
          "tehsil_name",
        ]),
        "District Name": getNestedValue(village, [
          "District.district_name",
          "district.district_name",
          "district_name",
        ]),
        Status: village.status === 1 ? "Active" : "In-Active",
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet["!cols"] = [
        { wch: 8 },
        { wch: 20 },
        { wch: 16 },
        { wch: 10 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 12 },
      ];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Villages");
      const currentDate = new Date().toISOString().split("T")[0];
      XLSX.writeFile(workbook, `Villages_Export_${currentDate}.xlsx`);
      toast.success(`Excel file exported successfully`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    } finally {
      setIsExporting(false);
    }
  };

  const getNestedValue = (obj, paths) => {
    for (const path of paths) {
      const keys = path.split(".");
      let value = obj;
      for (const key of keys) {
        value = value?.[key];
        if (value === undefined || value === null) break;
      }
      if (value !== undefined && value !== null) return value;
    }
    return "N/A";
  };

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  const columns = [
    {
      name: <span className="text-[0.813rem]">Sr.No</span>,
      cell: (row, index) => (
        <span>
          {(pagination.currentPage - 1) * pagination.pageSize + index + 1}
        </span>
      ),
      width: "80px",
      sortable: false,
    },
    {
      name: <span className="text-[0.813rem]">Village Name</span>,
      selector: (row) => row.village_name || "-",
      width: "280px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">VID</span>,
      selector: (row) => row.vid || "-",
      width: "160px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Type</span>,
      selector: (row) => row.type || "-",
      width: "100px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Deh Name</span>,
      selector: (row) =>
        getNestedValue(row, [
          "Deh.deh_name",
          "deh.deh_name",
          "Deh.Deh_Name",
          "deh.Deh_Name",
          "deh_name",
        ]),
      sortable: true,
    },
    // {
    //   name: <span className="text-[0.813rem]">UC Name</span>,
    //   selector: (row) =>
    //     getNestedValue(row, [
    //       "UC.uc_name",
    //       "uc.uc_name",
    //       "UC.UC_Name",
    //       "uc.UC_Name",
    //       "uc_name",
    //     ]),
    //   sortable: true,
    // },
    {
      name: <span className="text-[0.813rem]">Tehsil Name</span>,
      selector: (row) =>
        getNestedValue(row, [
          "Tehsil.tehsil_name",
          "tehsil.tehsil_name",
          "Tehsil.Tehsil_Name",
          "tehsil.Tehsil_Name",
          "tehsil_name",
        ]),
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">District Name</span>,
      selector: (row) =>
        getNestedValue(row, [
          "District.district_name",
          "district.district_name",
          "District.District_Name",
          "district.District_Name",
          "district_name",
        ]),
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Status</span>,
      width: "120px",
      cell: (row) => {
        const status = row.status;
        return (
          <span
            className={`py-1 px-2 text-[0.75em] rounded ${
              status === 1 ? "bg-success text-white" : "bg-red-500 text-white"
            }`}
          >
            {status === 1 ? "Active" : "In-Active"}
          </span>
        );
      },
    },
  ];
  {
    if (
      permissions.includes("village-update") ||
      permissions.includes("village-delete")
    ) {
      columns.push({
        name: <span className="text-[0.813rem]">Action</span>,
        cell: (row) => (
          <div className="flex gap-2">
            {/* <div className="flex gap-2">
          <Link to={`/villages/edit/${row.village_id}`}>
            <i className="fa-solid fa-pen-to-square fa-lg"></i>
          </Link>
        </div>
        <button
          onClick={() => showDeleteModal(row)}
          disabled={isDeleting}
          className={`${isDeleting ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <i
            className="fa fa-trash fa-lg text-primaryDark"
            aria-hidden="true"
          ></i>
        </button> */}

            <PermissionButton
              hasPermission={permissions.includes("village-update")}
              to={`/villages/edit/${row.village_id}`}
              faIcon="fa-solid fa-pen-to-square fa-lg"
              onlyIcon={true}
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed"
            />
            <PermissionButton
              hasPermission={permissions.includes("village-delete")}
              faIcon="fa fa-trash fa-lg text-primaryDark"
              onlyIcon
              onClick={() => showDeleteModal(row)}
              className="p-1 rounded-full bg-gray-200 text-[#137e81] hover:text-white"
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed bg-gray-300"
            />
          </div>
        ),
      });
    }
  }

  return (
    <div>
      {loader && <Loader />}
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            Village
          </h1>
          <div className="flex gap-2">
            <button
              onClick={exportToExcel}
              disabled={isExporting}
              className={`flex items-center gap-2 px-4 py-1 text-sm bg-[#137e81] hover:bg-green-700 text-white rounded-md transition-colors ${
                isExporting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <Download size={16} />{" "}
              {isExporting ? "Exporting..." : "Export Excel"}
            </button>
            <PermissionButton
              hasPermission={permissions.includes("village-create")}
              to="add"
              faIcon="fa fa-plus"
              text="Add"
              className="flex items-center gap-2 py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg:text-[15px] text-[12px]"
              enabledClass="bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85"
              disabledClass="bg-[#be9609] text-white opacity-50 cursor-not-allowed"
            />
          </div>
        </div>
        <Accordion>
          <AccordionSummary
            expandIcon={
              isFiltersVisible ? (
                <Minus color="#ffff" size={14} />
              ) : (
                <Plus size={14} color="#ffff" />
              )
            }
            onClick={toggleFilters}
            sx={{
              backgroundColor: "#137e81",
              color: "#fff !important",
              "& .MuiAccordionSummary-content": { color: "#fff !important" },
            }}
          >
            Filters
          </AccordionSummary>
          <AccordionDetails>
            <div className="flex gap-5 w-full">
              <ThinDropdownWithSearch required={false}
                label="District"
                options={districts?.map((d) => ({
                  value: d.district_id,
                  label: d.district_name,
                }))}
                value={tempDistrict}
                onChange={(val) => {
                  setTempDistrict(val);
                  setTempTehsil(null);
                  setTempUc(null);
                  setTempDeh(null);
                }}
              />
              <ThinDropdownWithSearch required={false}
                label="Tehsil"
                options={tehsils?.map((d) => ({
                  value: d.tehsil_id,
                  label: d.tehsil_name,
                }))}
                value={tempTehsil}
                onChange={(val) => {
                  setTempTehsil(val);
                  setTempUc(null);
                  setTempDeh(null);
                }}
              />
              {/* <ThinDropdownWithSearch required={false}
                label="UC"
                options={ucs?.map((d) => ({
                  value: d.uc_id,
                  label: d.uc_name,
                }))}
                value={tempUc}
                onChange={(val) => {
                  setTempUc(val);
                  setTempDeh(null);
                }}
              /> */}
              <ThinDropdownWithSearch required={false}
                label="Deh"
                options={dehs?.map((d) => ({
                  value: d.deh_id,
                  label: d.deh_name,
                }))}
                value={tempDeh}
                onChange={setTempDeh}
              />
              <div className="flex gap-2 items-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-[#be9609] text-white rounded hover:bg-[#997800]"
                >
                  Clear
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-[#137e81] text-white rounded hover:bg-[#0f5f5f]"
                >
                  Apply
                </button>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>

        {/* <br /> */}
        <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
          <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
            <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
              Village List
            </h1>
          </div>
          <div className="p-[1.25rem] w-full">
            <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-4 w-full">
              <PaginationDropdown
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                options={[
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 30, label: "30" },
                ]}
              />
              <input
                onChange={handleSearchChange}
                placeholder="Search..."
                type="search"
                className="text-[Light] flex items-center bg-[#ebeef2] border-[#ebeef2] rounded-md px-3 py-1 outline-none"
              />
            </div>

            <DataTable
              columns={columns}
              persistTableHead
              data={villages}
              customStyles={customStyles}
              highlightOnHover
            />
            <GeneralFooter
              pagination={pagination}
              handlePageChange={handlePageChange}
              handlePageSizeChange={handlePageSizeChange}
              dataLength={villages.length}
            />
          </div>
        </div>

        <CustomConfirmModal
          isVisible={isModalVisible}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          message={`Are you sure you want to delete the village "${selectedVillageForDelete?.name}"?`}
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
};

export default Village;
